import { useState } from 'react'
import * as stylex from '@stylexjs/stylex'

import ActivityToolbar, { type ActivityKindFilter } from '../../components/ActivityToolbar'
import Icon from '../../components/Icon'
import { findGroup, findPerson, formatActivityDate, formatMoney, type Overview } from '../../lib/ledger'

const styles = stylex.create({
  s0: { display: 'flex', flexDirection: 'column', rowGap: '28px' },
  s1: { fontSize: '13px', fontWeight: 500, color: '#5b9164' },
  s2: { marginTop: '4px', fontSize: 'clamp(1.75rem,3.2vw,2.25rem)', fontWeight: 600, letterSpacing: '-.045em' },
  s3: { marginTop: '8px', fontSize: '14px', color: '#707b72' },
  s4: { overflow: 'hidden', borderRadius: '1rem', borderWidth: 1, borderColor: '#e1e8df', backgroundColor: '#fff', boxShadow: '0 4px 16px rgba(48,70,51,.035)' },
  s5: { display: 'grid', width: '100%', gridTemplateColumns: 'auto minmax(0,1fr) auto', alignItems: 'center', gap: '12px', borderBottomWidth: 1, borderColor: '#edf0eb', paddingInline: '16px', paddingBlock: '16px', textAlign: 'left', transition: 'color, background-color, border-color, text-decoration-color, fill, stroke, opacity, box-shadow, transform, filter, backdrop-filter', ':hover': { backgroundColor: '#f8fbf7' }, ':last-child': { borderBottomColor: 'transparent' }, '@media (min-width: 640px)': { paddingInline: '20px' } },
  s6: { height: '20px', width: '20px' },
  s7: { minWidth: '0px' },
  s8: { display: 'block', overflow: 'hidden', whiteSpace: 'nowrap', textOverflow: 'ellipsis', fontSize: '14px', fontWeight: 600 },
  s9: { marginTop: '2px', display: 'block', overflow: 'hidden', whiteSpace: 'nowrap', textOverflow: 'ellipsis', fontSize: '12px', color: '#748077' },
  s10: { textAlign: 'right' },
  s11: { display: 'block', fontSize: '13px', fontWeight: 600, color: '#3d5542' },
  s12: { marginTop: '2px', display: 'block', fontSize: '11px', color: '#879087' },
  empty: { padding: '30px', textAlign: 'center', color: '#748077', fontSize: '13px' },
})

const activityStyles = stylex.create({
  icon: { display: 'flex', height: '40px', width: '40px', alignItems: 'center', justifyContent: 'center', borderRadius: '0.75rem' },
  expense: { backgroundColor: '#edf6eb', color: '#468554' },
  settlement: { backgroundColor: '#f5f0e7', color: '#997149' },
})

type ActivityPageProps = {
  data: Overview
  onOpenGroup: (groupID: string) => void
  onOpenExpense?: (expenseID: string) => void
}

type Activity = {
  amountCents: number
  createdAt: string
  detail: string
  expenseID?: string
  groupID?: string
  id: string
  kind: 'expense' | 'settlement'
  title: string
}

export default function ActivityPage({ data, onOpenGroup, onOpenExpense }: ActivityPageProps) {
  const [query, setQuery] = useState('')
  const [kind, setKind] = useState<ActivityKindFilter>('all')
  const activity: Activity[] = [
    ...data.expenses.map((expense) => {
      const payer = findPerson(data, expense.paidById)
      const group = findGroup(data, expense.groupId)
      return { id: expense.id, kind: 'expense' as const, createdAt: expense.occurredAt ?? expense.createdAt, amountCents: expense.amountCents, title: expense.description, detail: `${expense.paidById === data.currentUser.id ? 'You paid' : `${payer?.name ?? 'Someone'} paid`} in ${group?.name ?? 'a group'}`, groupID: expense.groupId, expenseID: expense.id }
    }),
    ...data.settlements.map((settlement) => {
      const person = findPerson(data, settlement.personId)
      const received = settlement.direction === 'received'
      return { id: settlement.id, kind: 'settlement' as const, createdAt: settlement.paidAt ?? settlement.createdAt, amountCents: settlement.amountCents, title: received ? 'Payment received' : 'Payment sent', detail: [received ? `${person?.name ?? 'Someone'} paid you` : `You paid ${person?.name ?? 'someone'}`, settlement.description].filter(Boolean).join(' · ') }
    }),
  ].sort((left, right) => new Date(right.createdAt).getTime() - new Date(left.createdAt).getTime())
  const normalizedQuery = query.trim().toLowerCase()
  const filteredActivity = activity.filter((item) => (kind === 'all' || item.kind === kind) && (!normalizedQuery || `${item.title} ${item.detail}`.toLowerCase().includes(normalizedQuery)))

  return <div {...stylex.props(styles.s0)}>
    <header><p {...stylex.props(styles.s1)}>The complete picture</p><h1 {...stylex.props(styles.s2)}>Activity</h1><p {...stylex.props(styles.s3)}>Every expense and payment, in the order it happened.</p></header>
    <ActivityToolbar kind={kind} onKindChange={setKind} onQueryChange={setQuery} query={query} />
    <section {...stylex.props(styles.s4)}>{filteredActivity.length === 0 ? <p {...stylex.props(styles.empty)}>No matching activity.</p> : filteredActivity.map((item) => <button {...stylex.props(styles.s5)} key={item.id} onClick={() => item.kind === 'expense' && item.expenseID ? onOpenExpense?.(item.expenseID) : item.groupID && onOpenGroup(item.groupID)} type="button"><span {...stylex.props(activityStyles.icon, item.kind === 'expense' ? activityStyles.expense : activityStyles.settlement)}><Icon {...stylex.props(styles.s6)} name={item.kind === 'expense' ? 'receipt' : 'settle'} /></span><span {...stylex.props(styles.s7)}><span {...stylex.props(styles.s8)}>{item.title}</span><span {...stylex.props(styles.s9)}>{item.detail}</span></span><span {...stylex.props(styles.s10)}><span {...stylex.props(styles.s11)}>{formatMoney(item.amountCents, data.currentUser.currency)}</span><span {...stylex.props(styles.s12)}>{formatActivityDate(item.createdAt)}</span></span></button>)}</section>
  </div>
}
